"use client";
import React, { useEffect, useState } from "react";
import CardWrapper from "../shared/pictureCard/cardWrapper";
import ImageComponentScroll from "./imageComponentScroll";
import ImageComponentAction from "./imageComponentAction";
import ImageComponentDescription from "./imageComponentDescription";
import { Expand } from "lucide-react";
import { getLocalizedPath } from "@/components/shared/formatURL";
import { useFullScreenModal } from "./full-screen-modal";
import Link from "next/link";
import Image from "next/image";
import { TriangleAlert } from "lucide-react";
import { useReportModal } from "./reportModal";
import { useRouter } from "next/navigation";
import { siteConfigurations } from "../shared/sites/sites";

interface UserData {
  username: string;
  userId: string;
}

export default function ImageComponent({
  action,
  _id,
  type = "image",
  translations,
  modalTranslations,
  reportModalTranslations,
  shareModalTranslations,
  signInModalTranslations,
}: {
  action: any;
  _id: string;
  type?: "image" | "gif";
  translations: any;
  modalTranslations: any;
  reportModalTranslations: any;
  shareModalTranslations: any;
  signInModalTranslations: any;
}) {
  const [userData, setUserData] = useState<UserData>({ username: "", userId: "" });
  const [reviewStatus, setReviewStatus] = useState("none");
  const { FullScreenModal, setFullScreenModal } = useFullScreenModal(action.post?.imageUrl);
  const { ReportModal, setShowReportModal } = useReportModal(reportModalTranslations);

  const localizedPostSearchPath = getLocalizedPath("post/search");
  const [views, setViews] = useState(action.post?.views);
  const reportAgainMsg = translations.reportAgain;
  const reportMsgParts = reportAgainMsg.split("<button>");

  const shouldShowPost = (status: string) => status === "none" || status === "approved";

  const router = useRouter();
  const [relatedPostResponse, setRelatedPostResponse] = useState([
    {
      _id: "",
      author: "",
      createdAt: "",
      imageUrl: "",
      videoUrl: "",
      thumbnailUrl: "",
      likes: 0,
      model: "",
      tagGroups: [],
      updatedAt: "",
      userLiked: false,
      views: 0,
    },
  ]);

  useEffect(() => {
    const fetchUsername = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
        if (!backendUrl) return;

        const response = await fetch(`${backendUrl}/post/username/${_id}`);
        if (!response.ok) {
          return; // Will use default empty strings
        }
        const data = await response.json();
        setUserData({
          username: data.username || "",
          userId: data.userId || "",
        });
      } catch (error) {
        // Will use default empty strings
        console.error("Error fetching username:", error);
      }
    };

    fetchUsername();
  }, [_id]);

  useEffect(() => {
    const fetchReviewStatus = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
        if (!backendUrl) return;

        const response = await fetch(`${backendUrl}/post/info/${_id}`);
        if (!response.ok) {
          return; // Will use default empty strings
        }
        const data = await response.json();
        setReviewStatus(data.reviewStatus || "none");
      } catch (error) {
        // Will use default empty strings
        console.error("Error fetching review status:", error);
      }
    };

    fetchReviewStatus();
  }, [_id]);

  useEffect(() => {
    const fetchPostViewData = async () => {
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
      if (!backendUrl) {
        return;
      }

      // Retrieve the viewed posts from localStorage
      const viewedPosts = JSON.parse(localStorage.getItem("viewedPosts") || "[]");

      // Check if the current post ID is already in the viewedPosts array
      if (viewedPosts.includes(_id)) {
        return; // If already viewed, exit early and do not increment the view count
      }

      try {
        const res = await fetch(`${backendUrl}/post/view/${_id}`);
        const json = await res.json();

        if (json.views !== undefined && json.views >= 0) {
          setViews(json.views);

          // Add the post ID to the beginning of the viewedPosts array
          viewedPosts.unshift(_id);

          // Ensure only the last 10 posts are stored
          if (viewedPosts.length > 10) {
            viewedPosts.pop(); // Remove the oldest post (last element in the array)
          }

          // Save the updated viewedPosts array back to localStorage
          localStorage.setItem("viewedPosts", JSON.stringify(viewedPosts));
        } else if (json.message !== undefined && json.message == "Post not found") {
          // Redirect to the home page or dashboard
          window.location.href = `${process.env.NEXT_PUBLIC_BASE_URL}`;
        }
      } catch (error) {
        // Redirect to the home page or dashboard
        window.location.href = `${process.env.NEXT_PUBLIC_BASE_URL}`;
      }
    };

    fetchPostViewData();
  }, [_id]); // Dependency array with _id ensures fetch runs only if _id changes

  useEffect(() => {
    const fetchRelatedPosts = async () => {
      try {
        const url = `${process.env.NEXT_PUBLIC_BACKEND_URL}/post/related/${_id}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "x-origin": process.env.NEXT_PUBLIC_BASE_URL || "",
          },
        });

        if (!response.ok) {
          router.push("/");
        }

        const data = await response.json();
        setRelatedPostResponse(data);
      } catch (error) {
        router.push("/");
      }
    };

    fetchRelatedPosts();
  }, [_id]);

  return (
    <>
      <FullScreenModal />
      <ReportModal />
      <div className="relative mt-[-23px] grid w-full grid-cols-1 px-0 sm:mt-0 sm:px-5 md:grid-cols-6">
        <div className="col-span-1 my-0 mr-0 block h-[60vh] overflow-hidden border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] shadow-lg sm:h-[70vh] sm:rounded-md md:col-span-4 md:mr-4">
          <div className="relative flex h-full w-full items-center justify-center">
            {!shouldShowPost(reviewStatus) ? (
              <div className="flex flex-col items-center justify-center text-lg font-semibold text-white">
                <div className="py-4">
                  <TriangleAlert color="#FFCC00" size={80} />
                </div>
                <div className="text-lg sm:text-xl md:text-2xl">{translations.underReview}</div>
                {/* Report again msg with report modal */}
                <div className="text-base sm:text-lg md:text-2xl">
                  {reportMsgParts[0]}
                  <button className="text-crp-red" onClick={() => setShowReportModal(true)}>
                    {reportMsgParts.length > 1 ? reportMsgParts[1].split("</button>")[0] : "here"}
                  </button>
                  {reportMsgParts.length > 1 ? reportMsgParts[1].split("</button>")[1] : null}
                </div>
              </div>
            ) : type === "gif" ? (
              <video
                className={`h-full w-full rounded-md object-contain ${
                  !shouldShowPost(reviewStatus) ? "hidden" : ""
                }`}
                src={action.post?.videoUrl}
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <Image
                className={`h-full w-full rounded-md object-cover ${
                  !shouldShowPost(reviewStatus) ? "hidden" : ""
                }`}
                src={action.post?.imageUrl}
                alt={action.tagStringProp}
                layout="fill"
                objectFit="contain"
                loading="eager"
                priority={true}
                unoptimized
              />
            )}
            <button
              aria-label="expand image"
              onClick={() => setFullScreenModal(true)}
              className={`absolute bottom-0 right-0 ${
                !shouldShowPost(reviewStatus) ? "hidden" : ""
              }`}
            >
              <Image
                src={"https://cdn6.imgpog.com/rectangle.svg"}
                className={`${!shouldShowPost(reviewStatus) ? "hidden" : ""}`}
                alt="expand"
                width={40}
                height={40}
              />
              <Expand color="#2B2D31" className="absolute bottom-0 right-0 h-5 w-auto pb-1 pr-1" />
            </button>
          </div>
        </div>
        <div className="col-span-1 mt-4 px-0 md:col-span-2 md:mt-0">
          <h1 className="mb-2 px-5 font-bold text-white sm:px-0">{action.tagStringProp}</h1>
          <div className="my-auto mb-4 ml-auto flex flex-row overflow-x-auto whitespace-nowrap px-5 sm:px-0">
            {shouldShowPost(reviewStatus) && (
              <>
                {userData.username === "" ? (
                  <div className="mr-1 text-xs text-white">Anonymous •</div>
                ) : (
                  <a
                    className="mr-1 text-xs text-white"
                    href={
                      process.env.NEXT_PUBLIC_BASE_URL +
                      translations.path +
                      "user/" +
                      userData.userId
                    }
                  >
                    {userData.username} •
                  </a>
                )}
              </>
            )}

            <h2 className="mr-1 text-xs text-white"> {views}</h2>
            <h2 className="mr-1 text-xs text-white">{translations.views}</h2>
            <h2 className="mr-1 text-xs text-white">
              • {action.post?.likes || 0} {translations.saves}
            </h2>
            <h2 className="mr-1 text-xs text-white">
              • {type === "gif" ? translations.AIPornGif : translations.AIPornPicture}
            </h2>
            {action.post?.aspectRatio && (
              <h2 className="mr-1 text-xs text-white">
                • {translations.aspectRatio}: {action.post?.aspectRatio}
              </h2>
            )}
          </div>
          <ImageComponentAction
            action={action}
            _id={_id}
            type={type}
            reviewStatus={reviewStatus}
            modalTranslations={modalTranslations}
            translations={translations}
            reportModalTranslations={reportModalTranslations}
            shareModalTranslations={shareModalTranslations}
            signInModalTranslations={signInModalTranslations}
          />
          <ImageComponentDescription
            type={type}
            action={action}
            reviewStatus={reviewStatus}
            translations={translations}
          />
        </div>
      </div>
      {type !== "gif" && (
        <>
          {(process.env.NEXT_PUBLIC_BASE_URL === "http://localhost:3000" ||
          process.env.NEXT_PUBLIC_BASE_URL === "https://www.8porn.ai"
            ? true
            : siteConfigurations[action.post?.origin]?.siteUrl ===
              process.env.NEXT_PUBLIC_BASE_URL) && (
            <>
              <div className="my-6 flex flex-row px-5">
                <h2 className="relative text-xl font-bold text-white">
                  {translations.SimilarAIPorn}
                </h2>
                <Link
                  prefetch={false}
                  href={localizedPostSearchPath + "?search=" + encodeURIComponent(action.randomTag)}
                  className={`ml-auto flex flex-row items-center justify-center rounded-full p-1.5 text-sm font-bold text-[#fff] backdrop-blur-xl hover:brightness-105`}
                >
                  {translations.MoreResults}
                </Link>
              </div>

              <div className="w-full px-0 sm:px-2">
                {relatedPostResponse.length > 0 ? (
                  <CardWrapper
                    data={relatedPostResponse}
                    contentType={type}
                    modalTranslations={modalTranslations}
                    signInModalTranslations={signInModalTranslations}
                  />
                ) : (
                  <p className="text-md font-bold text-white">{translations.NoRelatedPostsFound}</p>
                )}
              </div>
            </>
          )}
        </>
      )}
      <ImageComponentScroll />
    </>
  );
}
